/**
 * The number input for the whole task builder.
 *
 * Replaces six local sanitisers — `filterNumber` (duplicated verbatim in Numerical and Slider),
 * `filterPositiveNumber`, `filterCoordinate`, `filterNonNegInt`, `filterDecimal` and
 * `sanitizeThresholdInput` — which between them were three grammars crossed with a sign flag, plus
 * an optional domain that only Angle and Compass use.
 *
 * Deliberately NOT `<input type="number">`. Two existing comments in the codebase already argue
 * this and were ignored by a third site: a native number input hides typed-but-invalid text behind
 * an empty `value`, and its spinner is meaningless for an answer key.
 */
/* eslint-disable jsx-a11y/no-autofocus -- `autoFocus` is opt-in per call site and only ever set by
   a field created in response to a deliberate action (DataViz's grid adds a row), where focus
   following the new field is correct rather than a steal on load. File-scoped for the same reason
   as TextField's: the rule reports on the element and `return (` has no comment position. */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { applyGrammar, normalizeNumber, type NumberGrammar } from './field-kind';

import type { InputHTMLAttributes } from 'react';


import { fmtLocale, wantsScientific } from '@/components/task/number-format';
import { useDelayedStickyError } from '@/components/task/use-settled-value';

export interface NumberFieldProps {
  value: string;
  /** Receives the canonical form: one `.` separator, never a comma. */
  onChange: (value: string) => void;
  grammar: NumberGrammar;
  /** Latitude yes, a tolerance radius no. Was baked into six different function names. */
  allowNegative?: boolean;
  /**
   * Reported as an issue by the caller, never enforced here. Clamping at the keystroke would make
   * `360` unreachable whenever the value passes outside the range on the way to it.
   */
  domain?: { min: number; max: number };
  /** True when this value is faulty NOW. Timing — when to show it — is not the caller's business. */
  hasError?: boolean;
  /**
   * A softer state than `hasError`: the value is usable but questionable — a tolerance wider than
   * the band it sits in, say. Amber rather than the error colour, and outranked by `hasError`,
   * because a value cannot be both broken and merely suspect.
   */
  hasWarning?: boolean;
  /** A glyph before the value: the ± and −/+ that mark a tolerance's direction. */
  prefix?: string;
  /**
   * This field is the one currently in force — the sensor simulate bar uses it to show which
   * manual value is driving the simulation. An accent, not a fault, so it ranks below both
   * `hasError` and `hasWarning`: a field that is broken should look broken even while it is active.
   */
  active?: boolean;
  /** Id of the element explaining the current warning, wired to the input for screen readers. */
  describedBy?: string;
  /** Registers the field as a target for `focusIssueField`, so an issue can jump to it. */
  fieldId?: string;
  /** For a field that already has a real <label htmlFor>, so it keeps working. */
  id?: string;
  /** Optional when `id` is paired with a visible <label>; required otherwise. */
  ariaLabel?: string;
  placeholder?: string;
  suffix?: string;
  /** Kept from the raw inputs that had it — a hard cap on how much can be typed. */
  maxLength?: number;
  disabled?: boolean;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Enter in DataViz's last grid row adds another row — a keystroke the field does not own. */
  onKeyDown?: InputHTMLAttributes<HTMLInputElement>['onKeyDown'];
  /** For a field created in response to an action — DataViz's grid focuses a new row's first cell. */
  autoFocus?: boolean;
}

export function NumberField({
  value, onChange, grammar, allowNegative = false, domain, hasError, hasWarning, prefix, active, describedBy,
  fieldId, id, ariaLabel, placeholder, suffix, maxLength, disabled, className = '', onFocus, onBlur, onKeyDown, autoFocus,
}: NumberFieldProps) {
  /**
   * A SCIENTIFIC FIELD READS IN SCIENTIFIC NOTATION WHILE YOU ARE NOT IN IT.
   *
   * Typing 250000 left the box reading 250000 beside a chip reading 2.5e5 — one number, two
   * spellings, a few pixels apart, which is what the app's notation convention exists to stop.
   *
   * DISPLAY ONLY, and that distinction is the whole design. The first attempt rewrote the
   * stored string on blur, which agreed with the chip on notation and then disagreed with it
   * on PRECISION: `fmtLocale` caps display at six significant digits, so 123456789 showed as
   * `1.23456789e8` in the box and `1.23457e8` in the chip. Rounding the stored value to match
   * would have fixed that by throwing away digits the teacher typed — tolerable in a tolerance,
   * data loss in a dataviz cell or a variable bound.
   *
   * So the value underneath never changes, blur never fires `onChange`, and nothing is marked
   * dirty by tabbing through. Focus puts the real number back, which is what you edit.
   *
   * `integer` grammar is excluded by construction — an integer field has no business showing
   * an exponent.
   */
  const [focused, setFocused] = useState(false);
  const { i18n } = useTranslation();
  const shown = (() => {
    if (focused || grammar !== 'scientific') return value;
    /* `?? ''` is load-bearing: the prop is typed `string`, and a block whose version predates a
       field hands over `undefined` at runtime. The input tolerated that before this read it. */
    const raw = (value ?? '').trim();
    const n = Number(raw);
    if (raw === '' || !Number.isFinite(n) || !wantsScientific(n)) return value;
    return fmtLocale(n, i18n.language);
  })();

  /**
   * The same visibility rule every other field uses: a NEW fault waits for the value to stop
   * changing, an already-visible one stays lit until it is actually gone. Applied here rather than
   * by callers for the same reason FormulaField owns it — several callers pass an ungated flag, and
   * a caller that ALSO gates it defeats the sticky half (its flag goes false mid-typing, which is
   * indistinguishable from a fix, so the marker blinks and the delay restarts).
   */
  const showError = useDelayedStickyError(!!hasError, value);
  const border = showError ? 'border-ws-error-fg'
    : hasWarning ? 'border-ws-warning-fg'
    : active ? 'border-ws-primary'
    : 'border-ws-border';
  return (
    /* `data-field-id` goes on the BORDERED wrapper, not the input inside it. The highlight ring is
       an outer box-shadow, so tagging the input drew the ring within the field's own border —
       reported as the ring appearing inside the box instead of around it. Focus still lands on the
       input: `focusFieldElement` prefers an editable descendant over the tagged element itself. */
    /* `data-field-box` is the same statement for the accessibility panel's ring, which walks up to
       it rather than guessing which ancestor draws the border. */
    <span data-field-kind={grammar} data-field-id={fieldId} data-field-box='' className={`inline-flex items-center gap-1 rounded-md border ${border} bg-ws-btn px-2 py-1.5 ${className}`}>
      {prefix != null && (
        <span aria-hidden='true' className='shrink-0 select-none text-sm font-semibold text-ws-muted'>{prefix}</span>
      )}
      <input
        autoFocus={autoFocus}
        type='text'
        inputMode={grammar === 'integer' ? 'numeric' : 'decimal'}
        id={id}
        aria-label={ariaLabel}
        aria-invalid={showError || undefined}
        aria-describedby={describedBy}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        value={shown}
        onFocus={() => { setFocused(true); onFocus?.(); }}
        onBlur={() => { setFocused(false); onBlur?.(); }}
        onKeyDown={onKeyDown}
        onChange={e => onChange(normalizeNumber(applyGrammar(e.target.value, grammar, allowNegative)))}
        className='min-w-0 flex-1 bg-transparent text-sm text-ws-text outline-none placeholder:text-ws-muted'
      />
      {suffix != null && <span aria-hidden='true' className='shrink-0 text-xs text-ws-muted'>{suffix}</span>}
      {/* `domain` is intentionally unread here — see its doc comment. Named so a reader looking for
          the clamp finds the reason it is absent rather than assuming an oversight. */}
      {domain && null}
    </span>
  );
}
